/**
 * Session state.
 *
 * The token is the session. There is no refresh token yet (CORE-3 on the build
 * board), so a session lasts as long as the access token and then ends
 * cleanly — the user is told why, and lands back where they were after signing
 * in again, rather than being dumped on a blank dashboard.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError, api, readToken, setAuthFailureHandler, writeToken } from "./api";
import { device } from "./device";
import { wipe } from "./store";
import type { Role, TokenResponse } from "./types";

export interface Session {
  userId: string;
  orgId: string;
  role: Role;
  email: string;
  /** Somebody else chose their password; nothing else until they choose one. */
  mustChangePassword?: boolean;
  fullName?: string | null;
}

type Status = "checking" | "authenticated" | "anonymous";

interface AuthValue {
  status: Status;
  session: Session | null;
  /** Set when a session ended on its own, so the login page can explain why. */
  expired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Take a token handed back by first-run setup or a password change. */
  adopt: (token: TokenResponse, email: string) => void;
  signOut: () => void;
  can: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

const EMAIL_KEY = "keystone.email";

function sessionFrom(token: TokenResponse, email: string): Session {
  return {
    userId: token.user_id,
    orgId: token.org_id,
    role: token.role,
    email,
    mustChangePassword: Boolean(token.must_change_password),
    fullName: token.full_name ?? null,
  };
}


/**
 * Read a token's own claims, without the server.
 *
 * Used only when the server cannot be reached, and only to keep a tech signed
 * in to their own device. The signature is not checked here — it cannot be —
 * so this decides *nothing* about access: it decides what to draw while
 * offline. Every request still carries the token and is verified properly.
 *
 * Returns null for anything malformed or past its expiry, so a stale token
 * ends the session the way it should.
 */
function readClaims(token: string | null): { sub: string; org: string; role: Role } | null {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { sub?: string; org?: string; role?: string; exp?: number };

    if (!claims.sub || !claims.org) return null;
    if (typeof claims.exp === "number" && claims.exp * 1000 <= Date.now()) return null;

    return { sub: claims.sub, org: claims.org, role: (claims.role ?? "technician") as Role };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");
  const [session, setSession] = useState<Session | null>(null);
  const [expired, setExpired] = useState(false);

  const endSession = useCallback((wasExpiry: boolean) => {
    writeToken(null);
    setSession(null);
    setStatus("anonymous");
    setExpired(wasExpiry);
  }, []);

  // A 401 from any call ends the session once, wherever it happened.
  useEffect(() => {
    setAuthFailureHandler(() => endSession(true));
    return () => setAuthFailureHandler(null);
  }, [endSession]);

  // On load, a stored token is a claim, not proof. Verify it before showing
  // the app — otherwise the first screen renders and then collapses into a
  // login page, which reads as a bug.
  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!readToken()) {
        if (!cancelled) setStatus("anonymous");
        return;
      }
      try {
        const me = await api.auth.me();
        if (cancelled) return;
        let email = "";
        try {
          email = localStorage.getItem(EMAIL_KEY) ?? "";
        } catch {
          /* ignore */
        }
        setSession({
          userId: me.user_id,
          orgId: me.org_id,
          role: me.role,
          email: me.email ?? email,
          mustChangePassword: Boolean(me.must_change_password),
          fullName: me.full_name ?? null,
        });
        setStatus("authenticated");
      } catch (error) {
        if (cancelled) return;

        // Unreachable is not unauthorised. A tech who opens the app in a comms
        // room must not be signed out of their own device — they would lose
        // access to the working set and the queue, which is the exact moment
        // the app is supposed to be most useful. The stored token is trusted
        // locally instead, and only until its own expiry: it is a signed JWT,
        // so the device can read `exp` without asking anyone. Nothing is
        // granted by this — every real call is still verified server-side.
        if (error instanceof ApiError && error.status === 0) {
          const claims = readClaims(readToken());
          if (claims) {
            let email = "";
            try {
              email = localStorage.getItem(EMAIL_KEY) ?? "";
            } catch {
              /* ignore */
            }
            setSession({ userId: claims.sub, orgId: claims.org, role: claims.role, email });
            setStatus("authenticated");
            return;
          }
        }

        // An expired token is an ordinary end of session, not an error worth
        // an alarming message on first load.
        endSession(false);
      }
    }

    void verify();
    return () => {
      cancelled = true;
    };
  }, [endSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const token = await api.auth.login(email.trim().toLowerCase(), password, device().id);
    writeToken(token.access_token);
    try {
      localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
    } catch {
      /* ignore */
    }
    setSession(sessionFrom(token, email.trim().toLowerCase()));
    setExpired(false);
    setStatus("authenticated");
  }, []);

  const adopt = useCallback((token: TokenResponse, email: string) => {
    writeToken(token.access_token);
    try {
      localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
    } catch {
      /* ignore */
    }
    setSession(sessionFrom(token, email.trim().toLowerCase()));
    setExpired(false);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(() => {
    try {
      localStorage.removeItem(EMAIL_KEY);
    } catch {
      /* ignore */
    }
    // Forget the working set as well as the token. A shared van iPad handed to
    // the next tech must not still be carrying the last one's jobs, their
    // customers' access notes and their van's stock. Fire-and-forget: a store
    // that will not clear must not trap someone in a session.
    void wipe().catch(() => {});
    endSession(false);
  }, [endSession]);

  const can = useCallback(
    (...roles: Role[]) => (session ? roles.includes(session.role) : false),
    [session],
  );

  const value = useMemo<AuthValue>(
    () => ({ status, session, expired, signIn, adopt, signOut, can }),
    [status, session, expired, signIn, adopt, signOut, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}

/** Office-and-up. Techs see their own work; they do not reprice quotes. */
export const OFFICE: Role[] = ["owner", "office", "supervisor"];
